import {
  deadlineStatus,
  formatDate,
  formatMoney,
  isoMonthSchema,
  NOTICE_KIND_LABELS,
  type NoticeObligation,
  noticeText,
} from "@pcj/shared";
import { CalendarClock, RotateCcw } from "lucide-react";
import { useState } from "react";
import { Navigate, useParams } from "react-router";
import { MonthHeader } from "../../components/MonthFolders";
import { Banner, Button, CopyButton, EmptyState, Pill, Table, td, th } from "../../components/ui";
import { errorMessage } from "../../lib/api";
import { DEADLINE_TONES, deadlineLabel } from "../../lib/format";
import { useCompany } from "./CompanyLayout";
import { FilingDialog } from "./FilingDialog";

/** The notices (Avisos) of one reporting month (route `reports/:month`). */
export function NoticesMonthPage() {
  const { month = "" } = useParams();
  const state = useCompany();
  const { notices, clientById, operations, today, can, actions } = state;
  const [filing, setFiling] = useState<NoticeObligation | null>(null);
  const manage = can("manageCompliance");

  if (!isoMonthSchema.safeParse(month).success) return <Navigate to=".." relative="path" replace />;

  const monthNotices = notices.filter((n) => n.month === month).sort((a, b) => b.date.localeCompare(a.date));
  const folioOf = (notice: NoticeObligation) => operations.find((o) => o.id === notice.triggerOperationId)?.noticeFolio;
  const error = actions.updateOperation.error;

  return (
    <div className="space-y-4">
      <MonthHeader
        month={month}
        title="Avisos de"
        subtitle={monthNotices[0] ? `Fecha límite de presentación: ${formatDate(monthNotices[0].deadline)}` : undefined}
      />
      {error && !filing && <Banner tone="error">{errorMessage(error)}</Banner>}
      {monthNotices.length === 0 ? (
        <EmptyState icon={CalendarClock} title="Este mes no requirió Avisos">
          Revisa su informe en cero en la pestaña Avisos e informes.
        </EmptyState>
      ) : (
        <Table>
          <thead className="bg-gray-50">
            <tr>
              <th className={th}>Fecha</th>
              <th className={th}>Cliente</th>
              <th className={th}>Tipo</th>
              <th className={th}>Monto sin IVA</th>
              <th className={th}>Estatus</th>
              <th className={th} aria-label="Acciones" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {monthNotices.map((notice) => {
              const client = clientById.get(notice.clientId);
              const status = deadlineStatus(notice.deadline, today, notice.filed);
              return (
                <tr key={notice.id}>
                  <td className={`${td} whitespace-nowrap text-gray-600`}>{formatDate(notice.date)}</td>
                  <td className={`${td} font-medium text-gray-900`}>{client?.name ?? "—"}</td>
                  <td className={`${td} text-gray-600`}>{NOTICE_KIND_LABELS[notice.kind]}</td>
                  <td className={`${td} whitespace-nowrap text-gray-800`}>{formatMoney(notice.amount)}</td>
                  <td className={td}>
                    <Pill tone={DEADLINE_TONES[status]}>{deadlineLabel(status, notice.deadline, today)}</Pill>
                    {notice.filed && folioOf(notice) && (
                      <p className="mt-1 text-xs text-gray-500">Acuse {folioOf(notice)}</p>
                    )}
                    {!notice.filed && (
                      <p className="mt-1 text-xs text-gray-500">Límite {formatDate(notice.deadline)}</p>
                    )}
                  </td>
                  <td className={`${td} whitespace-nowrap text-right`}>
                    <div className="flex justify-end gap-2">
                      {client && (
                        <CopyButton
                          label="Copiar datos"
                          text={noticeText({ notice, client, company: state.company, operations })}
                        />
                      )}
                      {manage && !notice.filed && (
                        <Button size="sm" onClick={() => setFiling(notice)}>
                          Marcar presentado
                        </Button>
                      )}
                      {manage && notice.filed && (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={RotateCcw}
                          onClick={() =>
                            actions.updateOperation.mutate({
                              id: notice.triggerOperationId,
                              noticeFiled: false,
                              noticeFolio: null,
                            })
                          }
                        >
                          Deshacer
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <FilingDialog
        open={filing !== null}
        title="Registrar Aviso presentado"
        description="Registra el acuse que entrega el portal del SAT al presentar el Aviso."
        pending={actions.updateOperation.isPending}
        error={filing ? error : null}
        onClose={() => setFiling(null)}
        onConfirm={(folio) =>
          filing &&
          actions.updateOperation.mutate(
            { id: filing.triggerOperationId, noticeFiled: true, noticeFolio: folio },
            { onSuccess: () => setFiling(null) },
          )
        }
      />
    </div>
  );
}
